import React from 'react'
import { useTranslation } from 'react-i18next'

function StressorTags({ stressors }) {
  if (!stressors || stressors === '\u2014') {
    return <span className="text-gray-400">{'\u2014'}</span>
  }
  const items = stressors.split(', ').filter(Boolean)
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <span
          key={item}
          className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs"
        >
          {item}
        </span>
      ))}
    </div>
  )
}

export default function CoupleProfileSummary({ person1, person2 }) {
  const { t } = useTranslation('report')
  const p1Name = person1.respondent.name
  const p2Name = person2.respondent.name
  const p1 = person1.profile
  const p2 = person2.profile

  const PROFILE_FIELDS = [
    { key: 'knownPartner', label: t('coupleProfileSummary.fields.knownPartner') },
    { key: 'exclusiveRelationship', label: t('coupleProfileSummary.fields.exclusiveRelationship') },
    { key: 'relationshipStatus', label: t('coupleProfileSummary.fields.relationshipStatus') },
    { key: 'previouslyDivorced', label: t('coupleProfileSummary.fields.previouslyDivorced') },
    { key: 'viewOfMarriage', label: t('coupleProfileSummary.fields.viewOfMarriage') },
    { key: 'healthIssues', label: t('coupleProfileSummary.fields.healthIssues') },
  ]

  return (
    <div className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-6 shadow-sm border border-primary-100">
      <h2 className="text-lg font-bold text-gray-800 mb-4">{t('coupleProfileSummary.title')}</h2>

      {/* Name / Gender / Age cards */}
      <div className="flex gap-4 mb-5">
        <div className="flex-1 bg-white rounded-lg p-4 border border-gray-100">
          <div className="text-sm font-medium text-gray-500 mb-1">{p1Name}</div>
          <div className="text-sm text-gray-800">
            {t('coupleProfileSummary.yearsOld', { gender: p1.genderLabel, age: p1.age })}
          </div>
        </div>
        <div className="flex-1 bg-white rounded-lg p-4 border border-gray-100">
          <div className="text-sm font-medium text-gray-500 mb-1">{p2Name}</div>
          <div className="text-sm text-gray-800">
            {t('coupleProfileSummary.yearsOld', { gender: p2.genderLabel, age: p2.age })}
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="bg-white rounded-lg p-4 border border-gray-100">
        <div className="flex items-center pb-2 mb-2 border-b-2 border-gray-200">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-1/3">
            {t('coupleProfileSummary.relationshipContext')}
          </div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-1/3 text-center">
            {p1Name}
          </div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-1/3 text-center">
            {p2Name}
          </div>
        </div>

        {PROFILE_FIELDS.map(({ key, label }) => (
          <div
            key={key}
            className="flex items-start py-2 border-b border-gray-50 last:border-0"
          >
            <div className="text-sm text-gray-600 w-1/3">{label}</div>
            <div className="text-sm text-gray-800 w-1/3 text-center">{p1[key]}</div>
            <div className="text-sm text-gray-800 w-1/3 text-center">{p2[key]}</div>
          </div>
        ))}

        {/* Stressors row */}
        <div className="flex items-start py-3 border-t border-gray-100 mt-1">
          <div className="text-sm text-gray-600 w-1/3">{t('coupleProfileSummary.stressors')}</div>
          <div className="w-1/3 px-1">
            <StressorTags stressors={p1.stressors} />
          </div>
          <div className="w-1/3 px-1">
            <StressorTags stressors={p2.stressors} />
          </div>
        </div>
      </div>
    </div>
  )
}
